import React, { useId } from "react";
import { motion } from "motion/react";

interface BrandLogoProps {
  size?: "sm" | "md" | "lg";
  className?: string;
}

export default function BrandLogo({ size = "md", className = "" }: BrandLogoProps) {
  const rawId = useId();
  // Sanitize React useId (which contains colons like :r0:) into a valid SVG id
  const uniqueId = rawId.replace(/[^a-zA-Z0-9-_]/g, "_");
  const goldId = `cleanGold_${uniqueId}`;
  const borderId = `boxBorder_${uniqueId}`;

  const dimensions = {
    sm: "w-8 h-8",
    md: "w-10 h-10",
    lg: "w-12 h-12",
  };

  return (
    <motion.div
      className={`relative flex items-center justify-center shrink-0 select-none cursor-pointer group ${dimensions[size]} ${className}`}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
    >
      {/* Soft ambient halo glow behind the box on hover/active */}
      <div className="absolute inset-0 bg-brand-500/15 rounded-2xl filter blur-md opacity-0 group-hover:opacity-100 group-active:opacity-100 transition-opacity duration-300 pointer-events-none -z-10" />

      <svg
        viewBox="0 0 100 100"
        className="w-full h-full block overflow-visible"
        xmlns="http://www.w3.org/2000/svg"
        aria-label="Randon Brown Logo"
        role="img"
      >
        <defs>
          <linearGradient id={goldId} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#f59e0b" />
            <stop offset="100%" stopColor="#fbbf24" />
          </linearGradient>
          <linearGradient id={borderId} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#2e2e2e" />
            <stop offset="100%" stopColor="#454545" />
          </linearGradient>
        </defs>

        {/* 1. Structural Square Box Container */}
        <rect
          x="6"
          y="6"
          width="88"
          height="88"
          rx="22"
          fill="#0c0c0c"
          stroke={`url(#${borderId})`}
          strokeWidth="3.5"
          className="transition-all duration-300 group-hover:stroke-brand-500 group-hover:fill-[#141414] group-active:stroke-brand-400"
        />

        {/* 2. Inner clean "R" ribbon */}
        <motion.path
          d="M 36 74 L 36 26 L 56 26 C 68 26, 68 46, 56 46 L 36 46 M 48 46 L 64 74"
          fill="none"
          stroke={`url(#${goldId})`}
          strokeWidth="9"
          strokeLinecap="round"
          strokeLinejoin="round"
          initial={{ pathLength: 0.2, opacity: 0.6 }}
          animate={{ pathLength: 1, opacity: 1 }}
          transition={{
            duration: 0.9,
            ease: [0.16, 1, 0.3, 1],
          }}
          style={{
            // Fallback for browsers that fail SVG gradient stroke resolution
            stroke: `url(#${goldId}) #f59e0b`,
          }}
        />
      </svg>
    </motion.div>
  );
}
